import React, { useState, useEffect, useRef } from "react";
import { T } from "./Translate";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  serverTimestamp,
  orderBy,
  deleteDoc,
  setDoc,
  deleteField,
  increment,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../firebase";
import { toggleFollow } from "../utils/follow";
import PageHeader from "./PageHeader";
import { brandColors } from "../theme";
import {
  Container,
  Box,
  Avatar,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Divider,
  IconButton,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
  Fab,
  Dialog,
  Portal,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Favorite as FavoriteIcon,
  InsertEmoticon as EmojiIcon,
  Image as ImageIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Circle as CircleIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  Storefront as StorefrontIcon,
  MoreVert as MoreVertIcon,
} from "../icons";
import { blockUser, unblockUser, fetchBlockedIds } from "../utils/blockedUsers";
import { reportItem } from "./homeUtils";
import { saveContact, showsContact } from "../utils/contactPrivacy";
import CameraCapture from "../components/CameraCapture";
import { isMobileDevice } from "../utils/device";

// Abrevia contadores grandes (1200 -> "1.2K", 2000000 -> "2M") para que no
// rompan el layout del sidebar en perfiles con muchos seguidores.
const formatCount = (count) => {
  if (count >= 1_000_000) {
    const value = count / 1_000_000;
    return `${value % 1 === 0 ? value : value.toFixed(1)}M`;
  }
  if (count >= 1_000) {
    const value = count / 1_000;
    return `${value % 1 === 0 ? value : value.toFixed(1)}K`;
  }
  return `${count}`;
};

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  // --- ESTADOS ---
  const [user, setUser] = useState(null);
  const [stores, setStores] = useState([]);
  const [states, setStates] = useState([]);
  const [activities, setActivities] = useState([]);
  const [profileThreads, setProfileThreads] = useState([]);
  const [blockedIds, setBlockedIds] = useState([]);
  const [threadsMenu, setThreadsMenu] = useState(null);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [isCurrentUser, setIsCurrentUser] = useState(false);

  // Estados de UI y Formularios
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openStateDialog, setOpenStateDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedStateImage, setSelectedStateImage] = useState(null);
  const [newStateText, setNewStateText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    phone: "",
    point: "",
    professional: "",
    education: "",
    interests: "",
    profileImageUrl: "",
  });

  const fileInputRef = useRef(null);
  const stateFileInputRef = useRef(null);
  // Sacar la foto de la publicación con la cámara (mismo criterio que
  // Hilos.js): siempre en celulares/tablets, nunca en computadora.
  const [stateCameraOpen, setStateCameraOpen] = useState(false);
  const showCameraButton = isMobileDevice();
  const reactions = ["👍", "❤️", "😊", "😮", "😢", "😡"];
  // Cuentas protegidas guardan el teléfono en users/{uid}/private/contact en
  // vez del doc público (ver mismo mecanismo en edit.js). Si ese doc existe,
  // hay que leer/guardar el teléfono ahí y no en el doc público.
  const hasPrivateContactRef = useRef(false);

  // undefined = todavía no se resolvió el estado de auth, null = resuelto y sin sesión.
  const [authUid, setAuthUid] = useState(undefined);

  // --- EFECTOS DE CARGA ---

  // 0. Esperar a que Firebase Auth resuelva el usuario actual antes de usarlo
  // en queries/listeners que dependen de reglas de seguridad basadas en request.auth.
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => setAuthUid(u ? u.uid : null));
    return unsubscribe;
  }, []);

  // 1. Cargar datos del usuario.
  // Espera a que auth resuelva (igual que el resto de los efectos) y usa
  // onSnapshot en lugar de un getDoc único: en una recarga dura, un getDoc
  // aislado puede resolver contra una copia local desactualizada/incompleta
  // y nunca se corrige; con onSnapshot, en cuanto llega la versión real del
  // servidor el perfil se actualiza solo.
  useEffect(() => {
    if (!userId || !authUid) return;
    const unsubscribe = onSnapshot(
      doc(db, "users", userId),
      async (userDoc) => {
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUser({ id: userDoc.id, ...data });

          // Solo el dueño (o admin) puede leer users/{uid}/private/contact
          // (ver firestore.rules); para cualquier otro visitante ni se
          // intenta, así que el teléfono de una cuenta protegida queda
          // vacío en el form de edición como corresponde.
          let phone = data.phone || "";
          // Contacto en el doc privado salvo que el usuario eligió mostrarlo
          // (ver utils/contactPrivacy.js).
          hasPrivateContactRef.current = !showsContact(data);
          if (authUid === userId) {
            try {
              const privateContactSnap = await getDoc(
                doc(db, "users", userId, "private", "contact")
              );
              if (privateContactSnap.exists()) {
                if (data.mostrarContacto !== true) hasPrivateContactRef.current = true;
                phone = privateContactSnap.data().phone || "";
              }
            } catch (e) {
              console.error("Error fetching private contact:", e);
            }
          }

          setEditFormData({
            displayName: data.displayName || "",
            phone,
            point: data.point || "",
            professional: data.professional || "",
            education: data.education || "",
            interests: data.interests || "",
            profileImageUrl: data.profileImageUrl || "",
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching user:", error);
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [userId, authUid]);

  // 2. Cargar Tiendas
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "stores"), where("ownerId", "==", userId));
    getDocs(q).then((snap) =>
      setStores(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, [userId]);

  // 3. Cargar Estados (Real-time)
  useEffect(() => {
    if (!userId || !authUid) return;
    const q = query(collection(db, "states"), where("userId", "==", userId), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setStates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [userId, authUid]);

  // 4. Cargar Actividades
  useEffect(() => {
    if (!userId || !authUid) return;
    const q = query(collection(db, "activities"), where("userId", "==", userId), orderBy("timestamp", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setActivities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [userId, authUid]);

  // 4a. Cargar Hilos (ver Hilos.js), incluye sus republicaciones.
  useEffect(() => {
    if (!userId || !authUid) return;
    const q = query(collection(db, "threads"), where("uid", "==", userId), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setProfileThreads(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [userId, authUid]);

  // 4a-bis. Usuarios bloqueados por mí, para el menú de cada hilo.
  useEffect(() => {
    if (!authUid) return;
    fetchBlockedIds(authUid).then(setBlockedIds);
  }, [authUid]);

  // 4b. isCurrentUser depende de que auth ya haya resuelto, no solo del fetch del doc.
  useEffect(() => {
    if (authUid === undefined) return;
    setIsCurrentUser(authUid === userId);
  }, [authUid, userId]);

  // 5. Cargar Seguidores y Seguidos.
  // Usa el mismo esquema que perfil.js (documentos-mapa userFollowing/{uid} y
  // companyFollowers/{uid}), que es el único respaldado por firestore.rules —
  // las subcolecciones users/{uid}/following|followers nunca tuvieron reglas,
  // así que cualquier lectura ahí siempre devolvía permission-denied.
  useEffect(() => {
    if (!userId || !authUid) return;

    const resolveUsers = async (ids) => {
      const data = await Promise.all(ids.map(async (id) => {
        try {
          const uDoc = await getDoc(doc(db, "users", id));
          return uDoc.exists() ? { id: uDoc.id, ...uDoc.data() } : null;
        } catch (error) {
          // Las reglas de Firestore bloquean la lectura de cuentas protegidas
          // del dueño de la app para quien no sea el propio dueño/admin; se
          // omite ese usuario en vez de tirar toda la lista abajo.
          return null;
        }
      }));
      return data.filter(Boolean);
    };

    const unsubFollowing = onSnapshot(doc(db, "userFollowing", userId), async (snap) => {
      setFollowing(await resolveUsers(snap.exists() ? Object.keys(snap.data()) : []));
    });

    const unsubFollowers = onSnapshot(doc(db, "companyFollowers", userId), async (snap) => {
      setFollowers(await resolveUsers(snap.exists() ? Object.keys(snap.data()) : []));
    });

    return () => { unsubFollowing(); unsubFollowers(); };
  }, [userId, authUid]);

  // 6. Verificar si el usuario actual sigue a este perfil
  useEffect(() => {
    const checkStatus = async () => {
      if (!authUid || !userId || authUid === userId) return;
      const d = await getDoc(doc(db, "userFollowing", authUid));
      setIsFollowing(d.exists() && !!d.data()[userId]);
    };
    checkStatus();
  }, [userId, authUid]);

  // 7. Cuentas que el usuario logueado bloqueó (para el botón Bloquear/Desbloquear
  // y para no dejar mandar mensajes a alguien que uno mismo bloqueó).
  const [myBlockedUsers, setMyBlockedUsers] = useState({});
  useEffect(() => {
    if (!authUid || authUid === userId) return;
    const unsubscribe = onSnapshot(doc(db, "users", authUid), (snap) => {
      setMyBlockedUsers(snap.exists() ? (snap.data().blockedUsers || {}) : {});
    });
    return unsubscribe;
  }, [authUid, userId]);

  // 8. Solicitud de seguimiento pendiente (cuenta privada, ver toggleFollow
  // en utils/follow.js): mientras esté pendiente, el botón dice "Solicitud
  // enviada" y un segundo click la cancela.
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  useEffect(() => {
    if (!authUid || authUid === userId) return;
    const unsubscribe = onSnapshot(doc(db, "followRequests", userId), (snap) => {
      setHasPendingRequest(snap.exists() && !!snap.data()[authUid]);
    });
    return unsubscribe;
  }, [authUid, userId]);

  const iBlockedThem = !isCurrentUser && !!myBlockedUsers[userId];
  const theyBlockedMe = !isCurrentUser && !!(user?.blockedUsers?.[authUid]);
  const puedeVerSeguidores = isCurrentUser || user?.mostrarSeguidores !== false;
  const puedeEnviarMensaje = !isCurrentUser && !iBlockedThem && !theyBlockedMe && (
    user?.mensajesDe !== 'nadie' &&
    (user?.mensajesDe !== 'seguidos' || following.some((f) => f.id === authUid))
  );

  const handleToggleBlock = async () => {
    if (!authUid || !userId) return;
    try {
      if (iBlockedThem) {
        await updateDoc(doc(db, "users", authUid), { [`blockedUsers.${userId}`]: deleteField() });
      } else {
        await setDoc(doc(db, "users", authUid), { blockedUsers: { [userId]: true } }, { merge: true });
      }
    } catch (e) { console.error(e); }
  };

  // --- FUNCIONES DE LÓGICA ---

  const [blockMsg, setBlockMsg] = useState('');

  const handleStartDirectChat = async () => {
    if (!auth.currentUser || !userId) return;
    if (!puedeEnviarMensaje) {
      setBlockMsg(
        theyBlockedMe || iBlockedThem
          ? "No podés enviar mensajes a esta cuenta."
          : "Esta cuenta no acepta mensajes de vos."
      );
      return;
    }
    try {
      const currentUserId = auth.currentUser.uid;
      const sorted = [currentUserId, userId].sort();
      const conversationId = `directo_${sorted[0]}_${sorted[1]}`;
      const convRef = doc(db, "conversations", conversationId);
      const snap = await getDoc(convRef);

      if (!snap.exists()) {
        await setDoc(convRef, {
          type: "directo",
          participants: sorted,
          createdAt: serverTimestamp(),
          lastMessageTime: serverTimestamp(),
          unreadCount: { [currentUserId]: 0, [userId]: 0 },
        });
      }
      navigate(`/chatbox/directo/${userId}`);
    } catch (e) { console.error(e); }
  };

  // Registra una entrada en el feed de "Actividad" del perfil de actorUid.
  const logActivity = async (actorUid, content) => {
    try {
      await addDoc(collection(db, "activities"), {
        userId: actorUid,
        content,
        timestamp: serverTimestamp(),
      });
    } catch (e) { console.error("Error logging activity:", e); }
  };

  const handleFollowUnfollow = async () => {
    if (!authUid || !userId) return;
    try {
      const result = await toggleFollow({
        currentUid: authUid,
        currentDisplayName: auth.currentUser?.displayName,
        targetUid: userId,
        isFollowing,
        targetIsPrivate: user?.cuentaPrivada === true,
        hasPendingRequest,
      });
      if (result === "followed") {
        setIsFollowing(true);
        logActivity(authUid, `Empezó a seguir a ${user.displayName || "un usuario"}`);
      } else if (result === "unfollowed") {
        setIsFollowing(false);
      }
    } catch (e) { console.error(e); }
  };

  const handleCreateState = async () => {
    if ((!newStateText.trim() && !selectedStateImage) || !auth.currentUser) return;
    try {
      let imageUrl = null;
      if (selectedStateImage) {
        const sRef = ref(storage, `states/${auth.currentUser.uid}/${Date.now()}`);
        const snap = await uploadBytes(sRef, selectedStateImage);
        imageUrl = await getDownloadURL(snap.ref);
      }
      await addDoc(collection(db, "states"), {
        userId: auth.currentUser.uid,
        content: newStateText,
        imageUrl,
        createdAt: serverTimestamp(),
        reactions: {},
        userDisplayName: user.displayName,
        userProfileImage: user.profileImageUrl
      });
      logActivity(auth.currentUser.uid, newStateText.trim() ? `Publicó: "${newStateText.trim()}"` : "Publicó una foto");
      setOpenStateDialog(false);
      setNewStateText("");
      setSelectedStateImage(null);
    } catch (e) { console.error(e); }
  };

  const handleUpdateProfile = async () => {
    try {
      let imageUrl = editFormData.profileImageUrl;
      if (selectedImage) {
        const sRef = ref(storage, `profiles/${userId}`);
        const snap = await uploadBytes(sRef, selectedImage);
        imageUrl = await getDownloadURL(snap.ref);
      }

      const { phone, ...publicFormData } = editFormData;
      const updateData = { ...publicFormData, profileImageUrl: imageUrl };

      await updateDoc(doc(db, "users", userId), updateData);
      // El teléfono va al doc público o al privado según "Mostrar mi email y
      // teléfono" (ver utils/contactPrivacy.js).
      await saveContact(userId, { phone }, !hasPrivateContactRef.current);
      setUser(prev => ({ ...prev, ...editFormData, profileImageUrl: imageUrl }));
      logActivity(userId, "Actualizó su perfil");
      setOpenEditDialog(false);
    } catch (e) { console.error(e); }
  };

  const handleReaction = async (stateId, reaction) => {
    if (!auth.currentUser) return;
    const stateRef = doc(db, "states", stateId);
    const snap = await getDoc(stateRef);
    if (snap.exists()) {
      const current = snap.data().reactions || {};
      const uid = auth.currentUser.uid;
      if (current[uid] === reaction) {
        const { [uid]: _, ...rest } = current;
        await updateDoc(stateRef, { reactions: rest });
      } else {
        await updateDoc(stateRef, { reactions: { ...current, [uid]: reaction } });
      }
    }
  };

  const getReactionCounts = (reacs) => {
    const counts = {};
    Object.values(reacs || {}).forEach(r => counts[r] = (counts[r] || 0) + 1);
    return counts;
  };

  // El autor "real" del contenido mostrado: si es una republicación, quien
  // escribió el hilo original (no quien lo republicó).
  const threadContentAuthorUid = (threadData) =>
    threadData?.repostOf ? threadData.originalUid : threadData?.uid;

  const handleOpenThreadsMenu = (event, threadData) => {
    setThreadsMenu({ anchorEl: event.currentTarget, thread: threadData });
  };

  const handleThreadsMenuClose = () => setThreadsMenu(null);

  const handleDeleteThread = async (threadData) => {
    try {
      await deleteDoc(doc(db, "threads", threadData.id));
      if (threadData.repostOf) {
        await updateDoc(doc(db, "threads", threadData.repostOf), { repostsCount: increment(-1) });
      }
    } catch (error) {
      console.error("Error al eliminar el hilo:", error);
    } finally {
      handleThreadsMenuClose();
    }
  };

  const handleReportThread = (threadData) => {
    if (!threadData || !auth.currentUser) return;
    reportItem({
      reporterId: auth.currentUser.uid,
      targetType: "thread",
      targetId: threadData.id,
      companyId: threadContentAuthorUid(threadData),
    });
    handleThreadsMenuClose();
  };

  const handleToggleBlockThreadAuthor = async (threadData) => {
    const targetUid = threadContentAuthorUid(threadData);
    if (!targetUid || !auth.currentUser) return;
    try {
      if (blockedIds.includes(targetUid)) {
        await unblockUser(auth.currentUser.uid, targetUid);
        setBlockedIds((prev) => prev.filter((id) => id !== targetUid));
      } else {
        await blockUser(auth.currentUser.uid, targetUid);
        setBlockedIds((prev) => [...prev, targetUid]);
      }
    } catch (error) {
      console.error("Error al bloquear/desbloquear:", error);
    } finally {
      handleThreadsMenuClose();
    }
  };

  // --- RENDERIZADO ---

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
  if (!user) return <Container><Typography variant="h6" align="center" sx={{ mt: 4 }}><T>Usuario no encontrado</T></Typography></Container>;

  // Perfil Privado
  if (user.perfilPublico === false && !isCurrentUser) {
    return (
      <Container maxWidth="sm" sx={{ pb: 4 }}>
        <PageHeader title={user.displayName || <T>Usuario</T>} avatarSrc={user.profileImageUrl} />
        <Card sx={{ mt: 2, textAlign: 'center', p: 4 }}>
          <Avatar src={user.profileImageUrl} sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }} />
          <Typography variant="h5">{user.displayName}</Typography>
          <Typography color="textSecondary" sx={{ mt: 2, fontWeight: 'bold' }}><T>Perfil privado</T></Typography>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pb: 8 }}>
      <PageHeader title={user.displayName || <T>Usuario</T>} avatarSrc={user.profileImageUrl} />

      <Grid container spacing={3}>
        {/* COLUMNA IZQUIERDA: INFO PERSONAL */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 80 }}>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" sx={{ mb: 2 }}>
                <Avatar src={user.profileImageUrl} sx={{ width: 120, height: 120, mb: 2, border: 3, borderColor: "primary.main" }}>
                  <PersonIcon sx={{ fontSize: 60 }} />
                </Avatar>
                <Typography variant="h5" fontWeight="bold">{user.displayName || <T>Usuario</T>}</Typography>

                {puedeVerSeguidores ? (
                  <Box display="flex" gap={3} sx={{ mt: 2 }}>
                    <Box textAlign="center">
                      <Typography variant="h6" fontWeight="bold">{formatCount(following.length)}</Typography>
                      <Typography variant="caption" color="text.secondary"><T>Siguiendo</T></Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography variant="h6" fontWeight="bold">{formatCount(followers.length)}</Typography>
                      <Typography variant="caption" color="text.secondary"><T>Seguidores</T></Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
                    <T>Esta cuenta no muestra sus seguidores</T>
                  </Typography>
                )}

                <Box sx={{ mt: 3, width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {!isCurrentUser ? (
                    <>
                      <Button
                        variant={(isFollowing || hasPendingRequest) ? "outlined" : "contained"}
                        fullWidth
                        onClick={handleFollowUnfollow}
                        sx={(isFollowing || hasPendingRequest) ? { color: brandColors.accent, borderColor: "rgba(160,180,255,0.5)" } : undefined}
                      >
                        {isFollowing ? <T>Siguiendo</T> : hasPendingRequest ? <T>Solicitud enviada</T> : <T>Seguir</T>}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ChatIcon />}
                        fullWidth
                        onClick={handleStartDirectChat}
                        disabled={!puedeEnviarMensaje}
                        sx={{ color: brandColors.accent, borderColor: "rgba(160,180,255,0.5)" }}
                      >
                        <T>Enviar Mensaje</T>
                      </Button>
                      {blockMsg && <Typography variant="caption" color="error"><T>{blockMsg}</T></Typography>}
                      <Button
                        variant="text"
                        size="small"
                        fullWidth
                        onClick={handleToggleBlock}
                        color={iBlockedThem ? "primary" : "error"}
                      >
                        {iBlockedThem ? <T>Desbloquear</T> : <T>Bloquear</T>}
                      </Button>
                    </>
                  ) : (
                    <Button variant="contained" startIcon={<EditIcon />} fullWidth onClick={() => setOpenEditDialog(true)}>
                      <T>Editar Perfil</T>
                    </Button>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {user.phone && <Box display="flex" alignItems="center" gap={1}><PhoneIcon color="action" fontSize="small"/><Typography variant="body2">{user.phone}</Typography></Box>}
                {user.point && <Box display="flex" alignItems="center" gap={1}><LocationIcon color="action" fontSize="small"/><Typography variant="body2">{user.point}</Typography></Box>}
                {user.professional && <Box sx={{ mt: 1 }}><Typography variant="subtitle2" fontWeight="bold"><WorkIcon fontSize="inherit"/> <T>Profesional</T></Typography><Typography variant="body2" color="textSecondary">{user.professional}</Typography></Box>}
                {user.education && <Box sx={{ mt: 1 }}><Typography variant="subtitle2" fontWeight="bold"><SchoolIcon fontSize="inherit"/> <T>Educación</T></Typography><Typography variant="body2" color="textSecondary">{user.education}</Typography></Box>}
                {user.interests && <Box sx={{ mt: 1 }}><Typography variant="subtitle2" fontWeight="bold"><FavoriteIcon fontSize="inherit"/> <T>Intereses</T></Typography><Typography variant="body2" color="textSecondary">{user.interests}</Typography></Box>}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* COLUMNA DERECHA: TABS Y CONTENIDO */}
        <Grid item xs={12} md={8}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(e, v) => setTabValue(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              textColor="inherit"
              TabIndicatorProps={{ style: { backgroundColor: brandColors.accent } }}
              sx={{
                "& .MuiTab-root": { color: "text.secondary" },
                "& .Mui-selected": { color: brandColors.accent },
                "& .MuiTabs-scrollButtons": { color: brandColors.accent },
                "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.2 },
              }}
            >
              <Tab label={<T>Publicaciones</T>} />
              <Tab label={<T>Actividad</T>} />
              <Tab label={<T>Siguiendo</T>} />
              <Tab label={<T>Seguidores</T>} />
              <Tab label={<T>Tiendas</T>} />
              <Tab label={<T>Hilos</T>} />
            </Tabs>
          </Box>

          {/* CONTENIDO DE TABS */}
          <Box>
            {/* Tab 0: Publicaciones */}
            {tabValue === 0 && (
              <Box>
                {states.length > 0 ? states.map((state) => (
                  <Card key={state.id} sx={{ mb: 2 }}>
                    <CardHeader
                      avatar={<Avatar src={state.userProfileImage}><PersonIcon /></Avatar>}
                      action={isCurrentUser && <IconButton onClick={() => deleteDoc(doc(db, "states", state.id))}><DeleteIcon /></IconButton>}
                      title={state.userDisplayName}
                      subheader={state.createdAt ? new Date(state.createdAt.toMillis()).toLocaleString() : "Ahora"}
                    />
                    <CardContent>
                      <Typography variant="body1" sx={{ mb: 2 }}>{state.content}</Typography>
                      {state.imageUrl && <Box component="img" src={state.imageUrl} sx={{ width: "100%", maxHeight: 400, objectFit: "cover", borderRadius: 1 }} />}
                    </CardContent>
                    <CardActions>
                      {reactions.map((r) => (
                        <IconButton key={r} size="small" onClick={() => handleReaction(state.id, r)} sx={{ color: state.reactions?.[auth.currentUser?.uid] === r ? "primary.main" : "text.secondary" }}>
                          <Typography variant="body2">{r} {getReactionCounts(state.reactions)[r] || ""}</Typography>
                        </IconButton>
                      ))}
                    </CardActions>
                  </Card>
                )) : <Card><CardContent><Typography variant="body2" color="textSecondary"><T>No hay publicaciones aún</T></Typography></CardContent></Card>}
              </Box>
            )}

            {/* Tab 1: Actividad */}
            {tabValue === 1 && (
              <Card><CardContent>
                {activities.length > 0 ? activities.map((act) => (
                  <Box key={act.id} sx={{ mb: 2 }}>
                    <Box display="flex" alignItems="center">
                      <CircleIcon sx={{ fontSize: 8, mr: 1, color: "primary.main" }} />
                      <Typography variant="body2">{act.content}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      {act.timestamp ? new Date(act.timestamp.toMillis()).toLocaleString() : "Ahora"}
                    </Typography>
                    <Divider sx={{ mt: 1 }} />
                  </Box>
                )) : <Typography variant="body2" color="textSecondary"><T>No hay actividad reciente</T></Typography>}
              </CardContent></Card>
            )}

            {/* Tab 2 y 3: Seguidores/Siguiendo */}
            {(tabValue === 2 || tabValue === 3) && !puedeVerSeguidores && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                <T>Esta cuenta no muestra sus seguidores ni seguidos.</T>
              </Typography>
            )}
            {(tabValue === 2 || tabValue === 3) && puedeVerSeguidores && (
              <Grid container spacing={2}>
                {(tabValue === 2 ? following : followers).map((u) => (
                  <Grid item xs={12} sm={6} md={4} key={u.id}>
                    <Card sx={{ textAlign: 'center', p: 2 }}>
                      <Avatar src={u.profileImageUrl} sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }} />
                      <Typography variant="subtitle2" noWrap>{u.displayName}</Typography>
                      <Button size="small" onClick={() => navigate(`/user/${u.id}`)}><T>Ver Perfil</T></Button>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Tab 4: Tiendas */}
            {tabValue === 4 && (
              <Grid container spacing={2}>
                {stores.length > 0 ? stores.map((s) => (
                  <Grid item xs={12} sm={6} key={s.id}>
                    <Card sx={{ cursor: "pointer" }} onClick={() => navigate(`/stores/${s.slug}`)}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <StorefrontIcon sx={{ color: brandColors.accent }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight="bold" noWrap>{s.name || s.storeName}</Typography>
                            <Chip label={s.slug} size="small" variant="outlined" />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )) : <Typography variant="body2" sx={{ p: 2 }}><T>No hay tiendas publicadas</T></Typography>}
              </Grid>
            )}

            {/* Tab 5: Hilos */}
            {tabValue === 5 && (
              <Box>
                {profileThreads.length > 0 ? profileThreads.map((thread) => {
                  const isRepost = !!thread.repostOf;
                  const text = isRepost ? thread.originalText : thread.text;
                  const image = (isRepost ? thread.originalImages : thread.images)?.[0];
                  const reactionCount = Object.keys(thread.reactions || {}).length + (thread.likes || []).length;
                  return (
                    <Card key={thread.id} sx={{ mb: 2 }}>
                      {isRepost && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 1.5, px: 2 }}>
                          <T>Republicó</T>
                        </Typography>
                      )}
                      <CardHeader
                        action={
                          <IconButton onClick={(e) => handleOpenThreadsMenu(e, thread)}>
                            <MoreVertIcon />
                          </IconButton>
                        }
                        title={user.displayName}
                        subheader={thread.createdAt ? new Date(thread.createdAt.toMillis()).toLocaleString() : "Ahora"}
                      />
                      <CardContent sx={{ cursor: "pointer" }} onClick={() => navigate("/hilos")}>
                        {text && <Typography variant="body1" sx={{ mb: image ? 2 : 0 }}>{text}</Typography>}
                        {image && <Box component="img" src={image} sx={{ width: "100%", maxHeight: 400, objectFit: "cover", borderRadius: 1 }} />}
                      </CardContent>
                      <CardActions>
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                          {reactionCount} <T>reacciones</T> · {thread.commentsCount || 0} <T>comentarios</T>
                        </Typography>
                      </CardActions>
                    </Card>
                  );
                }) : <Card><CardContent><Typography variant="body2" color="textSecondary"><T>No hay hilos publicados aún</T></Typography></CardContent></Card>}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Typography
        variant="caption"
        align="center"
        sx={{ display: "block", color: brandColors.textSecondary, mt: 4, mb: 10 }}
      >
        © 2026 Dapur Tecnología - Notiline
      </Typography>

      {/* DIALOGS */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle><T>Editar Perfil</T></DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={selectedImage ? URL.createObjectURL(selectedImage) : editFormData.profileImageUrl} sx={{ width: 80, height: 80 }} />
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()} sx={{ color: brandColors.accent, borderColor: "rgba(160,180,255,0.5)" }}><T>Subir imagen</T></Button>
            <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={(e) => setSelectedImage(e.target.files[0])} />
          </Box>
          <TextField label={<T>Nombre</T>} fullWidth value={editFormData.displayName} onChange={(e) => setEditFormData({...editFormData, displayName: e.target.value})} />
          <TextField label={<T>Teléfono</T>} fullWidth value={editFormData.phone} onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})} />
          <TextField label={<T>Ubicación</T>} fullWidth value={editFormData.point} onChange={(e) => setEditFormData({...editFormData, point: e.target.value})} />
          <TextField label={<T>Profesional</T>} multiline rows={2} fullWidth value={editFormData.professional} onChange={(e) => setEditFormData({...editFormData, professional: e.target.value})} />
          <TextField label={<T>Educación</T>} multiline rows={2} fullWidth value={editFormData.education} onChange={(e) => setEditFormData({...editFormData, education: e.target.value})} />
          <TextField label={<T>Intereses</T>} multiline rows={2} fullWidth value={editFormData.interests} onChange={(e) => setEditFormData({...editFormData, interests: e.target.value})} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} sx={{ color: brandColors.accent }}><T>Cancelar</T></Button>
          <Button variant="contained" onClick={handleUpdateProfile} disabled={!editFormData.displayName}><T>Guardar</T></Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openStateDialog} onClose={() => setOpenStateDialog(false)} fullWidth maxWidth="sm" disableEnforceFocus={stateCameraOpen}>
        <DialogTitle><T>Crear publicación</T></DialogTitle>
        <DialogContent>
          <TextField autoFocus multiline rows={4} fullWidth placeholder="¿Qué estás pensando?" value={newStateText} onChange={(e) => setNewStateText(e.target.value)} sx={{ mt: 2 }} />
          {selectedStateImage && (
            <Box sx={{ mt: 2, position: "relative" }}>
              <img src={URL.createObjectURL(selectedStateImage)} alt="Preview" style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: 8 }} />
              <IconButton sx={{ position: "absolute", top: 8, right: 8, bgcolor: "background.paper" }} onClick={() => setSelectedStateImage(null)}><CloseIcon /></IconButton>
            </Box>
          )}
          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <input type="file" ref={stateFileInputRef} hidden accept="image/*" onChange={(e) => setSelectedStateImage(e.target.files[0])} />
            <Button startIcon={<ImageIcon />} variant="outlined" onClick={() => stateFileInputRef.current?.click()} sx={{ color: brandColors.accent, borderColor: "rgba(160,180,255,0.5)" }}><T>Foto</T></Button>
            {showCameraButton && (
              <Button startIcon={<PhotoCameraIcon />} variant="outlined" onClick={() => setStateCameraOpen(true)} sx={{ color: brandColors.accent, borderColor: "rgba(160,180,255,0.5)" }}><T>Cámara</T></Button>
            )}
            <Button startIcon={<EmojiIcon />} variant="outlined" onClick={() => setShowEmojiPicker(!showEmojiPicker)} sx={{ color: brandColors.accent, borderColor: "rgba(160,180,255,0.5)" }}><T>Emoji</T></Button>
          </Box>
          {showEmojiPicker && (
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {["😊", "😂", "😍", "👍", "❤️", "😎", "🎉", "🌟"].map(e => (
                <Grid item key={e}><Button onClick={() => setNewStateText(p => p + e)}>{e}</Button></Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStateDialog(false)} sx={{ color: brandColors.accent }}><T>Cancelar</T></Button>
          <Button variant="contained" onClick={handleCreateState} disabled={!newStateText.trim() && !selectedStateImage}><T>Publicar</T></Button>
        </DialogActions>
      </Dialog>

      {/* En un Portal montado después del Dialog: los dos usan zIndex 1300 y
          así la cámara queda por encima. */}
      {stateCameraOpen && (
        <Portal>
        <CameraCapture
          mode="photo"
          onCapture={(file) => {
            setSelectedStateImage(file);
            setStateCameraOpen(false);
          }}
          onClose={() => setStateCameraOpen(false)}
        />
        </Portal>
      )}

      <Menu
        anchorEl={threadsMenu?.anchorEl}
        open={Boolean(threadsMenu)}
        onClose={handleThreadsMenuClose}
      >
        {threadsMenu && threadContentAuthorUid(threadsMenu.thread) !== authUid && (
          <MenuItem onClick={() => handleReportThread(threadsMenu.thread)}>
            <T>Reportar</T>
          </MenuItem>
        )}
        {threadsMenu && threadContentAuthorUid(threadsMenu.thread) !== authUid && (
          blockedIds.includes(threadContentAuthorUid(threadsMenu.thread)) ? (
            <MenuItem onClick={() => handleToggleBlockThreadAuthor(threadsMenu.thread)}>
              <T>Desbloquear</T>
            </MenuItem>
          ) : (
            <MenuItem onClick={() => handleToggleBlockThreadAuthor(threadsMenu.thread)}>
              <T>Bloquear</T>
            </MenuItem>
          )
        )}
        {threadsMenu && threadsMenu.thread.uid === authUid && (
          <MenuItem onClick={() => handleDeleteThread(threadsMenu.thread)} sx={{ color: "#ff6b6b" }}>
            <T>Eliminar</T>
          </MenuItem>
        )}
      </Menu>

      {isCurrentUser && (
        // "position: fixed" ancla al viewport, no al Container maxWidth="lg" centrado
        // de esta pagina: en pantallas anchas quedaba pegado al borde de la ventana,
        // lejos del contenido real. Este wrapper reproduce el mismo ancho/centrado
        // del Container para que el boton quede alineado a la derecha del contenido.
        <Box
          sx={{
            position: "fixed",
            bottom: 32,
            left: "var(--sidebar-w, 0px)",
            right: 0,
            display: "flex",
            justifyContent: "flex-end",
            pointerEvents: "none",
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: (theme) => theme.breakpoints.values.lg,
              mx: "auto",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Fab color="primary" onClick={() => setOpenStateDialog(true)} sx={{ pointerEvents: "auto" }}>
              <AddIcon />
            </Fab>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default UserProfile;
